import React, { use, useState, useMemo } from 'react'
import { ApiClientContext, Clients, createClients } from './contextDeclaration'
import { ApiConfig } from './http-client-base'

interface Props {
  children: React.ReactNode
  clients: Partial<Clients>
  /** If there are no parent provider, use these clients instead, used in fire-session package */
  fallbackClients?: Partial<Clients>
}

/**
 * @deprecated Please use ApiClientProviderV2
 */
export const ApiClientProvider = ({ children, clients, fallbackClients }: Props) => {
  const inheritedClients = use(ApiClientContext)
  if (fallbackClients) {
    if (!inheritedClients) {
      return <ApiClientContext value={{ ...fallbackClients }}>{children}</ApiClientContext>
    } else {
      return (
        <ApiClientContext value={{ ...inheritedClients, ...clients }}>{children}</ApiClientContext>
      )
    }
  }
  return <ApiClientContext value={clients}>{children}</ApiClientContext>
}

interface V2Props {
  children?: React.ReactNode
  apiConfig: ApiConfig
}

export const ApiClientProviderV2 = ({ children, apiConfig }: V2Props) => {
  const [clients] = useState(() => createClients(apiConfig))
  return <ApiClientContext value={clients}>{children}</ApiClientContext>
}

export const useApiClient = <T extends keyof Clients>() => {
  const value = use(ApiClientContext) as Pick<Clients, T> | null
  if (value === null) {
    throw new Error('Attempted to call useApiClient without ApiClientProvider')
  }

  const proxiedValue = useMemo(() => {
    // This is needed because usually the provided client is used inside react-query,
    // which ends up swallowing the error. With this, we throw an error right when accessing
    // or destructing the client from the context. It will only throw for accessed/destructed
    // properties.
    return new Proxy(value, {
      get: (target, prop) => {
        const parsedProp = prop as keyof typeof target
        const targetValue = target[parsedProp]
        if (targetValue) {
          return targetValue
        }
        if (typeof parsedProp === 'string') {
          throw new Error(
            `Attempted to consume ${parsedProp} client but it was not provided to ApiClientProvider. Try using ApiClientProviderV2!`
          )
        }
      },
    })
  }, [value])

  return proxiedValue
}
